import { useState, forwardRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { InputAdornment, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { useSitesSelected, useTreeContext } from '../../../TreeProvider'
import { useSites, ACCOUNTS_LIMIT, countUserSites, useUser } from 'store'
import { useParams } from 'react-router-dom'
import Counter from './Counter'

import {
  StyledTextField,
  StyledLocationAdornment,
  StyledClearIcon,
  StyledStack,
  StyledIconButton
} from './SearchField.styles'

const SearchField = forwardRef(
  ({ placeholder, disabled, maxLabelWidth, showCountChip = true, onChange = () => {}, onFocus = () => {} }, ref) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [user] = useUser()
    const [verbatim, setVerbatim] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [isPopperOpen, setIsPopperOpen] = useState(false)
    const { selection, onNodeRemoveAll, canRemoveSelection } = useTreeContext()
    const { count: numberOfSitesSelected, firstSite } = useSitesSelected({ selection })
    const [, setShowVerbatim] = useState(true)
    const [{ selected: sites }] = useSites()
    const { focusId } = useParams()

    const totalSites = useMemo(() => countUserSites(user), [user])
    const showChevron = totalSites > ACCOUNTS_LIMIT && numberOfSitesSelected > 0

    const selectedSite = useMemo(() => {
      return sites.find(({ sf_id }) => sf_id === focusId)
    }, [sites, focusId])

    const handleCounterClick = () => {
      setShowVerbatim(false)
      setVerbatim('')
    }

    const handleVerbatimClick = () => {
      setShowVerbatim(true)
      onChange(verbatim)
    }

    const handleClearAll = () => {
      onChange('')
      setVerbatim('')
      onNodeRemoveAll()
      setShowVerbatim(true)
    }

    const handleFocus = e => {
      setIsFocused(true)
      setIsPopperOpen(true)
      setShowVerbatim(true)
      onFocus(e)
    }

    const handleBlur = () => {
      setIsFocused(false)
      setIsPopperOpen(false)
      setVerbatim('')
    }

    return (
      <StyledTextField
        inputRef={ref}
        disabled={disabled}
        id="input-with-icon-adornment"
        variant="outlined"
        value={isFocused ? verbatim : ''}
        autoComplete="off"
        placeholder={
          numberOfSitesSelected > 0
            ? isFocused
              ? placeholder || t('ui_elements.search_global')
              : ''
            : placeholder || t('ui_elements.search_global')
        }
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={e => {
          setVerbatim(e.target.value)
          onChange(e.target.value)
        }}
        sx={{
          '& .MuiInputBase-input': {
            paddingLeft: '8px'
          }
        }}
        InputProps={{
          startAdornment: (
            <StyledStack direction="row" spacing={1} alignItems="center">
              <StyledLocationAdornment isfocused={isFocused ? 1 : 0}>
                <i
                  className={`${numberOfSitesSelected > 0 ? 'fa-solid' : 'fa-light'} fa-location-dot`}
                  style={{
                    color: numberOfSitesSelected > 0 ? '#2570EB' : undefined
                  }}
                />
              </StyledLocationAdornment>
              {showCountChip && numberOfSitesSelected > 0 && (
                <Counter
                  count={numberOfSitesSelected}
                  firstSite={firstSite}
                  onClick={handleCounterClick}
                  selectedSite={selectedSite}
                  maxWidth={maxLabelWidth}
                  exceedsLimit={showChevron}
                />
              )}
              {showCountChip && numberOfSitesSelected > 1 && selectedSite && (
                <>
                  <Typography variant="caption" color="text.body.secondary">
                    {'>'}
                  </Typography>
                  <Typography
                    variant="caption"
                    onClick={handleVerbatimClick}
                    title={selectedSite.name || selectedSite.label}
                    sx={{
                      cursor: 'pointer',
                      color: theme.palette.primary.main,
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    {selectedSite.name || selectedSite.label}
                  </Typography>
                </>
              )}
            </StyledStack>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row" spacing={1} alignItems="center">
                {showChevron && (
                  <IconButton
                    size="small"
                    sx={{
                      color: '#7A8096',
                      fontSize: '14px',
                      padding: 0
                    }}>
                    <i className={`fa-solid fa-chevron-${isPopperOpen ? 'up' : 'down'}`} />
                  </IconButton>
                )}
                {canRemoveSelection && (verbatim || numberOfSitesSelected > 0) && (
                  <StyledIconButton
                    type="button"
                    aria-label={t('ui_elements.clear', { defaultValue: 'Clear' })}
                    onMouseDown={e => e.preventDefault()}
                    onClick={handleClearAll}>
                    <StyledClearIcon className="fa-thin fa-xmark" />
                  </StyledIconButton>
                )}
              </Stack>
            </InputAdornment>
          )
        }}
      />
    )
  }
)

export default SearchField
